/**
 * @vitest-environment jsdom
 *
 * Adversarial test corpus for the sanitize-html primitives. Cases are sourced
 * from public XSS cheatsheets (OWASP, PortSwigger, html5sec.org) and from
 * past DOMPurify CVEs / advisories. They exercise the wrappers, not DOMPurify
 * itself — the assertion in every case is "the rendered string does not
 * carry an executable payload through to a DOM that would fire on hydration".
 *
 * The shape of each assertion is intentionally strict: we don't trust that
 * "alert" or "javascript" not appearing as a substring means safety, so most
 * checks combine "tag/attr stripped" + "no live-handler attribute survives"
 * + "raw text content may survive but is rendered as text".
 */

import { describe, expect, it } from "vitest";

import {
  sanitizeInlineMarkup,
  sanitizeMarketingHtml,
  sanitizeRichText,
} from "./sanitize-html";

// Helper: parse the sanitised string into a real DOM, then assert no element
// in the resulting tree carries an attribute whose name starts with `on`,
// nor an `href`/`src` whose value resolves to anything beyond the allowlist.
const parseDom = (html: string) => {
  const wrapper = document.createElement("div");
  wrapper.innerHTML = html;
  return wrapper;
};

const eventHandlerAttrsOf = (root: HTMLElement) => {
  const offenders: string[] = [];
  root.querySelectorAll("*").forEach((el) => {
    for (const attr of Array.from(el.attributes)) {
      if (attr.name.toLowerCase().startsWith("on")) {
        offenders.push(`${el.tagName}:${attr.name}`);
      }
    }
  });
  return offenders;
};

const dangerousProtocols = /^(javascript|data|vbscript|file|blob):/i;
const dangerousHrefsIn = (root: HTMLElement) => {
  const offenders: string[] = [];
  root.querySelectorAll<HTMLAnchorElement>("[href]").forEach((el) => {
    if (dangerousProtocols.test(el.getAttribute("href") ?? "")) {
      offenders.push(el.getAttribute("href") ?? "");
    }
  });
  root.querySelectorAll<HTMLImageElement>("[src]").forEach((el) => {
    if (dangerousProtocols.test(el.getAttribute("src") ?? "")) {
      offenders.push(el.getAttribute("src") ?? "");
    }
  });
  return offenders;
};

const scriptTagsIn = (root: HTMLElement) =>
  Array.from(root.querySelectorAll("script"));

describe("sanitizeInlineMarkup", () => {
  describe("baseline allowlist behaviour", () => {
    it("keeps same-origin relative links", () => {
      expect(sanitizeInlineMarkup('<a href="/dashboard">go</a>')).toBe(
        '<a href="/dashboard">go</a>',
      );
    });

    it("keeps a data-method attribute on a link (Rails UJS)", () => {
      const out = sanitizeInlineMarkup(
        '<a href="/logout" data-method="delete">Log out</a>',
      );
      expect(out).toContain('data-method="delete"');
    });

    it("strips disallowed tags but keeps their text content", () => {
      expect(sanitizeInlineMarkup("<b>bold</b>")).toBe("bold");
    });

    it("returns an empty string for null and undefined", () => {
      expect(sanitizeInlineMarkup(null)).toBe("");
      expect(sanitizeInlineMarkup(undefined)).toBe("");
    });

    it("returns an empty string for empty input", () => {
      expect(sanitizeInlineMarkup("")).toBe("");
    });
  });

  describe("script-tag injection", () => {
    it.each([
      "<script>alert(1)</script>",
      "<SCRIPT>alert(1)</SCRIPT>",
      "<ScRiPt>alert(1)</ScRiPt>",
      "<script src=//evil.com/x.js></script>",
      "<script\nsrc=//evil.com/x.js></script>",
      "<script type='text/javascript'>alert(1)</script>",
      "<script	>alert(1)</script>",
    ])("strips '%s'", (payload) => {
      const out = sanitizeInlineMarkup(payload + "safe");
      expect(scriptTagsIn(parseDom(out))).toHaveLength(0);
      expect(out).toContain("safe");
    });
  });

  describe("event-handler injection", () => {
    it.each([
      '<a href="/x" onmouseover="alert(1)">l</a>',
      '<a href="/x" onclick="alert(1)">l</a>',
      '<a href="/x" onfocus="alert(1)" autofocus>l</a>',
      '<a href="/x" oNmOuSeOvEr="alert(1)">l</a>',
      "<a href=\"/x\" onmouseover='alert(1)'>l</a>",
      // Unquoted attribute value
      "<a href=/x onmouseover=alert(1)>l</a>",
      // Tab between attribute name and equals
      "<a\thref=/x\tonmouseover=alert(1)>l</a>",
      // Newline between attribute name and value
      "<a href=/x\nonmouseover=alert(1)>l</a>",
      // Backtick around value (some parsers accept)
      "<a href=/x onmouseover=`alert(1)`>l</a>",
    ])("strips event handlers in '%s'", (payload) => {
      const out = sanitizeInlineMarkup(payload);
      expect(eventHandlerAttrsOf(parseDom(out))).toEqual([]);
    });
  });

  describe("javascript:/data:/vbscript: URI bypasses", () => {
    it.each([
      "javascript:alert(1)",
      "JaVaScRiPt:alert(1)",
      // Tab in scheme (browsers normalise away whitespace inside URLs)
      "java\tscript:alert(1)",
      // Newline in scheme
      "java\nscript:alert(1)",
      // Carriage-return in scheme
      "java\rscript:alert(1)",
      // Null byte in scheme
      "java\0script:alert(1)",
      // HTML-entity-encoded colon
      "javascript&colon;alert(1)",
      // HTML decimal entity
      "&#x6A;avascript:alert(1)",
      // Long form decimal
      "&#0000106;avascript:alert(1)",
      // vbscript
      "vbscript:msgbox(1)",
      // data: with embedded script
      "data:text/html,<script>alert(1)</script>",
      "data:text/html;base64,PHNjcmlwdD5hbGVydCgxKTwvc2NyaXB0Pg==",
      // file:
      "file:///etc/passwd",
      // blob:
      "blob:https://example.com/abc",
    ])("strips href='%s'", (href) => {
      const out = sanitizeInlineMarkup(`<a href="${href}">x</a>`);
      expect(dangerousHrefsIn(parseDom(out))).toEqual([]);
    });
  });

  describe("authority-delimiter / protocol-relative bypasses", () => {
    it.each([
      "//evil.com",
      "///evil.com",
      "/\\evil.com", // CRITICAL: slash-backslash is authority-delimiter in WHATWG
      "/\\\\evil.com", // slash + two backslashes
      "/\\/evil.com", // mixed slash-backslash
      // URL-encoded backslash - browsers may or may not normalise
      "/%5cevil.com",
    ])("strips href='%s'", (href) => {
      const out = sanitizeInlineMarkup(`<a href="${href}">click</a>`);
      const root = parseDom(out);
      // Either the href is gone entirely, or it doesn't resolve to a foreign host
      root.querySelectorAll<HTMLAnchorElement>("a[href]").forEach((a) => {
        const raw = a.getAttribute("href") ?? "";
        // A surviving href must start with a single slash followed by a non-slash,
        // non-backslash character.
        expect(raw).toMatch(/^\/[^/\\]/);
      });
    });
  });

  describe("DOMPurify historical mXSS / mutation vectors", () => {
    // Mutation XSS via noscript - DOMPurify has had advisories around these
    it("strips noscript-wrapped HTML injection", () => {
      const payload =
        '<noscript><p title="</noscript><img src=x onerror=alert(1)>">';
      const out = sanitizeInlineMarkup(payload);
      const root = parseDom(out);
      expect(scriptTagsIn(root)).toHaveLength(0);
      expect(eventHandlerAttrsOf(root)).toEqual([]);
      expect(root.querySelector("img")).toBeNull();
    });

    it("strips template / svg foreignObject smuggling", () => {
      const payload =
        "<template><svg><foreignObject><body onload=alert(1)>x</body></foreignObject></svg></template>";
      const out = sanitizeInlineMarkup(payload);
      expect(eventHandlerAttrsOf(parseDom(out))).toEqual([]);
    });

    it("strips MathML annotation-xml encoded HTML", () => {
      const payload =
        '<math><annotation-xml encoding="text/html"><iframe src="javascript:alert(1)"></iframe></annotation-xml></math>';
      const out = sanitizeInlineMarkup(payload);
      const root = parseDom(out);
      expect(root.querySelector("iframe")).toBeNull();
      expect(dangerousHrefsIn(root)).toEqual([]);
    });
  });
});

describe("sanitizeRichText", () => {
  describe("baseline allowlist behaviour", () => {
    it("keeps the allowlisted inline tags", () => {
      const input =
        '<p>Hello <strong>world</strong>, <em>see</em> <a href="/x">x</a><br>and <b>more</b>.</p>';
      expect(sanitizeRichText(input)).toBe(input);
    });

    it("strips img tags (not in allowlist) but keeps trailing text", () => {
      const out = sanitizeRichText('<img src=x onerror="alert(1)">trail');
      const root = parseDom(out);
      expect(root.querySelector("img")).toBeNull();
      expect(eventHandlerAttrsOf(root)).toEqual([]);
      expect(out).toContain("trail");
    });
  });

  describe("script + event-handler corpus (symmetric with sanitizeInlineMarkup)", () => {
    it.each([
      "<script>alert(1)</script>",
      "<SCRIPT>alert(1)</SCRIPT>",
      '<p onclick="alert(1)">x</p>',
      '<a href="/x" onmouseover="alert(1)">l</a>',
      '<strong onerror="alert(1)">x</strong>',
    ])("strips '%s'", (payload) => {
      const out = sanitizeRichText(payload + "tail");
      const root = parseDom(out);
      expect(scriptTagsIn(root)).toHaveLength(0);
      expect(eventHandlerAttrsOf(root)).toEqual([]);
      expect(out).toContain("tail");
    });
  });

  describe("URI bypasses (symmetric with sanitizeInlineMarkup)", () => {
    it.each([
      "javascript:alert(1)",
      "JaVaScRiPt:alert(1)",
      "java\tscript:alert(1)",
      "javascript&colon;alert(1)",
      "&#x6A;avascript:alert(1)",
      "data:text/html,<script>alert(1)</script>",
      "vbscript:msgbox(1)",
      "https://evil.com",
      "//evil.com",
      "///evil.com",
      "/\\evil.com",
      "/\\\\evil.com",
    ])("strips href='%s'", (href) => {
      const out = sanitizeRichText(`<a href="${href}">x</a>`);
      const root = parseDom(out);
      expect(dangerousHrefsIn(root)).toEqual([]);
      root.querySelectorAll<HTMLAnchorElement>("a[href]").forEach((a) => {
        expect(a.getAttribute("href")).toMatch(/^\/[^/\\]/);
      });
    });
  });

  describe("disallowed-but-tempting tags", () => {
    it.each([
      "<iframe src=javascript:alert(1)></iframe>",
      "<object data=javascript:alert(1)></object>",
      "<embed src=javascript:alert(1)></embed>",
      "<svg><script>alert(1)</script></svg>",
      "<svg onload=alert(1)></svg>",
      "<details ontoggle=alert(1) open>x</details>",
      "<marquee onstart=alert(1)>x</marquee>",
      "<video><source onerror=alert(1)></video>",
      "<form><input type=image src=x onerror=alert(1)></form>",
      "<isindex action=javascript:alert(1) type=image>",
      "<base href=javascript:alert(1)//>",
      "<meta http-equiv=refresh content=0;url=javascript:alert(1)>",
    ])("strips '%s'", (payload) => {
      const out = sanitizeRichText(payload + "ok");
      const root = parseDom(out);
      expect(scriptTagsIn(root)).toHaveLength(0);
      expect(eventHandlerAttrsOf(root)).toEqual([]);
      expect(dangerousHrefsIn(root)).toEqual([]);
      [
        "iframe",
        "object",
        "embed",
        "svg",
        "details",
        "marquee",
        "video",
        "source",
        "form",
        "input",
        "isindex",
        "base",
        "meta",
      ].forEach((t) => expect(root.querySelector(t)).toBeNull());
    });
  });
});

describe("sanitizeMarketingHtml", () => {
  describe("baseline allowlist behaviour", () => {
    it("keeps headings, lists, code, blockquote", () => {
      const input =
        "<h2>Title</h2><ul><li>One</li></ul><pre><code>code</code></pre><blockquote>quote</blockquote>";
      expect(sanitizeMarketingHtml(input)).toBe(input);
    });

    it("allows external https links", () => {
      const out = sanitizeMarketingHtml('<a href="https://ably.com">go</a>');
      expect(out).toContain('href="https://ably.com"');
    });

    it("allows http and relative URIs on img src", () => {
      const out = sanitizeMarketingHtml(
        '<img src="https://ably.com/x.png" alt="x">',
      );
      expect(out).toContain('src="https://ably.com/x.png"');
    });

    it("preserves figure/figcaption around images (Ghost-style)", () => {
      const input =
        '<figure><img src="/x.png" alt="x"><figcaption>caption</figcaption></figure>';
      expect(sanitizeMarketingHtml(input)).toBe(input);
    });
  });

  describe("script and embed stripping", () => {
    it.each([
      "<script>alert(1)</script>",
      '<script src="https://js.hscta.net/cta/current.js"></script>',
      '<script type="application/ld+json">{"x":1}</script>',
      "<iframe src=https://youtube.com></iframe>",
      "<iframe src=javascript:alert(1)></iframe>",
      '<object data="https://evil.com"></object>',
      "<embed src=javascript:alert(1)>",
      "<form action=javascript:alert(1)><input></form>",
      "<svg><script>alert(1)</script></svg>",
      "<svg onload=alert(1)></svg>",
      "<math><mtext><img src=x onerror=alert(1)></mtext></math>",
    ])("strips '%s' while keeping trailing copy", (payload) => {
      const out = sanitizeMarketingHtml(payload + "copy");
      const root = parseDom(out);
      expect(scriptTagsIn(root)).toHaveLength(0);
      expect(eventHandlerAttrsOf(root)).toEqual([]);
      expect(dangerousHrefsIn(root)).toEqual([]);
      [
        "iframe",
        "object",
        "embed",
        "form",
        "input",
        "svg",
        "script",
        "math",
      ].forEach((t) => expect(root.querySelector(t)).toBeNull());
      expect(out).toContain("copy");
    });
  });

  describe("URL-protocol bypasses on allowed-tag attributes", () => {
    it.each([
      "javascript:alert(1)",
      "JaVaScRiPt:alert(1)",
      "java\tscript:alert(1)",
      "java\nscript:alert(1)",
      "javascript&colon;alert(1)",
      "&#x6A;avascript:alert(1)",
      "&#0000106;avascript:alert(1)",
      "data:text/html,<script>alert(1)</script>",
      "vbscript:msgbox(1)",
      "file:///etc/passwd",
      "blob:https://example.com/abc",
    ])("strips href='%s' on <a>", (href) => {
      const out = sanitizeMarketingHtml(`<a href="${href}">x</a>`);
      expect(dangerousHrefsIn(parseDom(out))).toEqual([]);
    });

    it.each([
      "javascript:alert(1)",
      "data:image/svg+xml,<svg onload=alert(1)>",
      "vbscript:msgbox(1)",
    ])("strips img src='%s'", (src) => {
      const out = sanitizeMarketingHtml(`<img src="${src}" alt="x">`);
      expect(dangerousHrefsIn(parseDom(out))).toEqual([]);
    });
  });

  describe("authority-delimiter / protocol-relative bypasses", () => {
    // Same corpus as the inline/rich-text wrappers - the marketing wrapper
    // earlier permitted `//evil.com` because the URI regex was unanchored
    // past the leading slash. Symmetric coverage protects against regression.
    it.each([
      "//evil.com",
      "///evil.com",
      "/\\evil.com",
      "/\\\\evil.com",
      "/\\/evil.com",
      "/%5cevil.com",
    ])("strips href='%s' on <a>", (href) => {
      const out = sanitizeMarketingHtml(`<a href="${href}">click</a>`);
      const root = parseDom(out);
      root.querySelectorAll<HTMLAnchorElement>("a[href]").forEach((a) => {
        const raw = a.getAttribute("href") ?? "";
        // A surviving href must be either a full http(s):// URL or a
        // single-slash same-origin path. No protocol-relative variants.
        expect(raw).toMatch(/^(https?:\/\/|\/[^/\\])/);
      });
    });

    it.each(["//evil.com/x.png", "/\\evil.com/x.png", "/\\\\evil.com/x.png"])(
      "strips img src='%s'",
      (src) => {
        const out = sanitizeMarketingHtml(`<img src="${src}" alt="x">`);
        const root = parseDom(out);
        root.querySelectorAll<HTMLImageElement>("img[src]").forEach((img) => {
          expect(img.getAttribute("src")).toMatch(/^(https?:\/\/|\/[^/\\])/);
        });
      },
    );
  });

  describe("style-based payloads", () => {
    it("strips style attribute entirely (we do not allow it)", () => {
      const out = sanitizeMarketingHtml(
        '<p style="background:url(javascript:alert(1))">x</p>',
      );
      expect(out).not.toContain("style");
      expect(out).not.toContain("javascript:");
    });

    it("strips inline style with expression()", () => {
      const out = sanitizeMarketingHtml(
        '<p style="width:expression(alert(1))">x</p>',
      );
      expect(out).not.toContain("style");
      expect(out).not.toContain("expression");
    });
  });

  describe("event-handler corpus on allowed tags", () => {
    it.each([
      '<p onclick="alert(1)">x</p>',
      '<a href="/x" onmouseover="alert(1)">l</a>',
      '<img src="/x.png" onerror="alert(1)" alt="x">',
      '<h2 onmouseenter="alert(1)">t</h2>',
      '<table onclick="alert(1)"><tbody><tr><td>x</td></tr></tbody></table>',
    ])("strips event handlers in '%s'", (payload) => {
      const out = sanitizeMarketingHtml(payload);
      expect(eventHandlerAttrsOf(parseDom(out))).toEqual([]);
    });
  });

  describe("mXSS / parser-confusion vectors", () => {
    it("strips noscript-wrapped img injection", () => {
      const payload =
        '<noscript><p title="</noscript><img src=x onerror=alert(1)>">';
      const out = sanitizeMarketingHtml(payload);
      const root = parseDom(out);
      expect(eventHandlerAttrsOf(root)).toEqual([]);
      const img = root.querySelector("img");
      // If an <img> survives (it's allowed in marketing) it must not carry onerror
      if (img) {
        expect(img.getAttribute("onerror")).toBeNull();
        expect(img.getAttribute("src")).not.toBe("x"); // src=x with no scheme is fine, but the onerror must be gone
      }
    });

    it("strips xlink:href javascript on svg use", () => {
      const out = sanitizeMarketingHtml(
        '<svg><use xlink:href="javascript:alert(1)"></use></svg>',
      );
      const root = parseDom(out);
      expect(root.querySelector("svg")).toBeNull();
      expect(dangerousHrefsIn(root)).toEqual([]);
    });

    it("strips title-attribute escape inside an allowed tag", () => {
      const payload =
        '<a href="/x" title=\'"><script>alert(1)</script><p \'>link</a>';
      const out = sanitizeMarketingHtml(payload);
      expect(scriptTagsIn(parseDom(out))).toHaveLength(0);
    });
  });
});
